import React from 'react';
import Link from 'next/link';
import Modal from '@common/modal';
import {
  getResultTypeText,
  moveToResults,
} from '@common/header/messageModal/messages/companyMessage/resultRequest/resultModals';
import Icon from '@components/common/assets';
import GradeBadge from '@components/match/common/gradeBadge';
import EmptyMatchResultSelectModal from '@components/match/profile/profileCard/matchResultSelectModal/EmptyMatchResultSelectModal';
import { PaccProgressCompleteVO } from '@domain/vo/phs/pacc/results/PaccProgressCompleteListVO';
import { PstProgressCompleteVO } from '@domain/vo/phs/pst/result/PstProgressCompleteListVO';
import { AccProgressCompleteVO } from '@domain/vo/acca/test/AccProgressCompleteListVO';
import { MatchingTalkApplyType } from '@domain/rs/match/message/MatchingMessageListRs';
import Colors from '@domain/constant/colors';
import useOnMount from '@utils/hooks/useOnMount';

import classnames from 'classnames/bind';
import styles from './matchResultSelectModal.module.scss';

const cx = classnames.bind(styles);

interface ResultItemProps {
  resultType: MatchingTalkApplyType | null;
  resultData: AccProgressCompleteVO | PaccProgressCompleteVO | PstProgressCompleteVO;
  isChecked: (resultSn: number) => boolean;
  onClick: (resultSn: number) => void;
  setSelectedResultSn: React.Dispatch<React.SetStateAction<number | null>>;
}

const ResultItem = ({ resultType, resultData, isChecked, onClick, setSelectedResultSn }: ResultItemProps) => {
  const { sn, result, applyEndDateTime, matchingUseYn } = resultData;
  const isPST = resultType === 'PST';

  useOnMount(() => {
    if (matchingUseYn) {
      setSelectedResultSn(sn);
    }
  });

  if (result === '분석 실패') return null;

  const isAnalysis = result === '분석중';

  return (
    <li
      className={cx('listItem', { on: isChecked(sn), isPST, isAnalysis })}
      onClick={() => {
        if (isAnalysis) return;
        onClick(sn);
      }}
    >
      <div className={cx('infoArea')}>
        <div>
          {matchingUseYn && (
            <div className={cx('verified')}>
              <Icon name="verifiedLightFilled" width={32} height={32} fill={Colors.C_GREEN_150} />
              잡다매칭 결과표
            </div>
          )}
          <div className={cx('infoWrap')}>
            <span className={cx('time')}>{applyEndDateTime}</span>
            {(isPST || isAnalysis) && <GradeBadge grade={result} className={cx('badge')} />}
          </div>
        </div>

        <div className={cx('linkArea')}>
          {isAnalysis ? (
            <a className={cx('resultLink', { disabled: isAnalysis })} role="button">
              결과 자세히 보기
            </a>
          ) : (
            <Link href={moveToResults(resultType, sn)} legacyBehavior>
              <a className={cx('resultLink')} target="_blank" role="button">
                결과 자세히 보기
              </a>
            </Link>
          )}
        </div>
      </div>
    </li>
  );
};

interface IMatchResultSelectModalProps {
  selectedResultSn: number | null;
  setSelectedResultSn: React.Dispatch<React.SetStateAction<number | null>>;
  resultsData: AccProgressCompleteVO[] | PaccProgressCompleteVO[] | PstProgressCompleteVO[] | null;
  applyType: MatchingTalkApplyType | null;
  onClose: () => void;
  onConfirm: () => void;
  isVisible: boolean;
}

const MatchResultSelectModal = ({
  selectedResultSn,
  setSelectedResultSn,
  resultsData,
  applyType: resultType,
  onClose,
  onConfirm,
  isVisible,
}: IMatchResultSelectModalProps) => {
  const isResultData = resultsData?.filter(({ result }) => result !== '분석 실패').length;
  const isSelected = selectedResultSn !== null;

  const resultTitle = `나의 ${getResultTypeText(resultType)} 결과`;
  const emptyModalText = `${getResultTypeText(resultType)} 결과가 없습니다.`;

  const isChecked = (resultSn: number) => selectedResultSn === resultSn;

  const checkResult = (resultSn: number) => {
    if (selectedResultSn === resultSn) setSelectedResultSn(null);
    else setSelectedResultSn(resultSn);
  };

  const resultItemEl = resultsData?.map((resultData) => (
    <ResultItem
      key={resultData.sn}
      resultType={resultType}
      isChecked={isChecked}
      onClick={checkResult}
      resultData={resultData}
      setSelectedResultSn={setSelectedResultSn}
    />
  ));

  if (!isResultData)
    return <EmptyMatchResultSelectModal isVisible={isVisible} onClose={onClose} applyType={resultType} />;

  return (
    <Modal visible={isVisible} dimmed={true} isUseScrollHidden={false}>
      <div className={cx('layer')}>
        <strong className={cx('title')}>잡다매칭에 사용할 결과표를 선택해 주세요.</strong>

        <div className={cx('resultArea')}>
          {/* [D] 결과에 따라 텍스트 변경해주세요. */}
          <em className={cx('resultTitle')}>{resultTitle}</em>

          <div className={cx('resultListWrap')}>
            <ul className={cx('resultList')}>
              {/* [D] 결과 자세히 보기 링크랑 겹쳐서 해당 요소가 클릭 요소 역할도 합니다. */}
              {/* [D] 선택시 'on' 클래스 추가해주세요. */}
              {resultItemEl}
            </ul>
          </div>
        </div>

        <div className={cx('btnArea')}>
          {/* [D] 리스트 선택된 경우 disabled={false}로 변경해주세요. */}
          <button className={cx('btn')} type="button" disabled={!isSelected} onClick={onConfirm}>
            선택 완료
          </button>
        </div>

        <a className={cx('btnClose')} role="button" onClick={onClose}>
          <span className={cx('hidden')}>close</span>
        </a>
      </div>
    </Modal>
  );
};

export default MatchResultSelectModal;
